import { useEffect, useMemo, useState } from 'react';
import { fetchAssetSummaryData } from '../../../api/fetchAssets';
import type { Asset } from '../../../models/asset';

export interface AssetAccount {
    key: string;
    name: string;
}

export interface AccountOption extends AssetAccount {
    count: number;
}

const CHUNK_SIZE = 2000;
const CONCURRENCY = 3;
const UPDATE_EVERY_CHUNKS = 5;
const accountCache = new Map<string, AssetAccount | null>();

const toAccount = (accountId?: string, accountName?: string): AssetAccount | null => {
    const name = accountName && accountName !== '-' ? accountName.trim() : '';
    if (!name) {
        return null;
    }
    return { key: accountId || name, name };
};

export const clearAssetAccountsCache = () => accountCache.clear();

const useAssetAccounts = (assets: Asset[], enabled = true) => {
    const [version, setVersion] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            return;
        }
        const missing = assets.filter(asset => !accountCache.has(asset.id)).map(asset => asset.id);
        if (!missing.length) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        const chunks: string[][] = [];
        for (let i = 0; i < missing.length; i += CHUNK_SIZE) {
            chunks.push(missing.slice(i, i + CHUNK_SIZE));
        }

        let pendingUpdates = 0;
        const worker = async () => {
            while (chunks.length && !cancelled) {
                const ids = chunks.shift();
                if (!ids) {
                    return;
                }
                const data = await fetchAssetSummaryData(ids);
                if (cancelled || !data.length) {
                    continue;
                }
                const byId = new Map(data.map(item => [item.id, item]));
                for (const id of ids) {
                    const item = byId.get(id);
                    accountCache.set(id, toAccount(item?.accountId, item?.accountName));
                }
                pendingUpdates += 1;
                if (pendingUpdates >= UPDATE_EVERY_CHUNKS) {
                    pendingUpdates = 0;
                    setVersion(value => value + 1);
                }
            }
        };

        Promise.all(Array.from({ length: CONCURRENCY }, worker)).then(() => {
            if (!cancelled) {
                setVersion(value => value + 1);
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [assets, enabled]);

    const accountByAsset = useMemo(() => {
        const map = new Map<string, AssetAccount>();
        if (!enabled) {
            return map;
        }
        for (const asset of assets) {
            const account = accountCache.get(asset.id);
            if (account) {
                map.set(asset.id, account);
            }
        }
        return map;
    }, [assets, version, enabled]);

    const accounts = useMemo<AccountOption[]>(() => {
        const byKey = new Map<string, AccountOption>();
        for (const account of accountByAsset.values()) {
            const current = byKey.get(account.key);
            byKey.set(account.key, current ? { ...current, count: current.count + 1 } : { ...account, count: 1 });
        }
        return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [accountByAsset]);

    const accountNameByKey = useMemo(() => new Map(accounts.map(account => [account.key, account.name])), [accounts]);

    return { accountByAsset, accounts, accountNameByKey, loading };
};

export default useAssetAccounts;
